import { useId } from 'react';
import { Link } from 'react-router-dom';
import { BiInfoCircle } from 'react-icons/bi';

import Humanograma from 'components/Humanograma';
import { Selo } from 'components/icons';
import Button from 'components/ui/button';
import Dica from 'components/ui/dica';
import { UserAvatar } from 'components/user-avatar';
import { AJUDA_DOS_PONTOS_JOGA_JUNTO, AJUDA_DOS_PONTOS_REALIZACOES } from 'components/pontos-help-modal/textos';
import { useModal } from 'context/ModalContext';
import { getLevelDescription, getLevelTitle, isLevelReached, levels, levelType } from 'services/utils';
import { FormatNumber, isGrupoValido } from 'utils/helpers';
import { PerfilNivel } from 'hooks/usePerfilNivel';

// Matrícula, função e acordo no tamanho do design, numa linha só (PH2-01, substitui o AJ2-05).
// SPEC_DEVIATION: o pedido é o valor no ciano dos pontos. Reason: a 11px o ciano dá 3,02:1 e o amarelo 4,44:1
// sobre o azul do cartão, abaixo do 4,5:1 de texto pequeno (WCAG 1.4.3); o branco negrito dá 4,88:1.
const DADO_DO_FUNCI = 'text-jj-label leading-tight text-brand-white';
const VALOR_DO_FUNCI = 'font-bold text-brand-white';

const LEVEL_BARS = [
  { level: 'joga', label: '#JogandoJunto' },
  { level: 'valeu', label: '#Valeu' },
  { level: 'mandou', label: '#MandouBem' },
  { level: 'uau', label: '#Uau' },
];

// Nome legível de cada selo, para o texto acessível do link. As chaves são os
// `behavior` gravados pelo `get_selos` e os dois selos sem comportamento.
const SEAL_NAMES: Record<string, string> = {
  colaboracao: 'Colaboração',
  foco: 'Foco no Cliente',
  inovacao: 'Inovação',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança',
  Realizacao: 'Realização Destaque',
  Galeria: 'Galeria do Reconhecimento',
};

/** Selo mais recente, no formato do `get_selos`. */
export interface Seal {
  behavior?: string;
  type?: unknown;
  cycle?: string;
}

/** Nome do asset do `Selo`: o comportamento com inicial maiúscula, ou Realização/Galeria. */
function sealKey(seal: Seal) {
  if (seal.behavior) return seal.behavior;

  return seal.type ? 'Realizacao' : 'Galeria';
}

function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function InfoTip({ content, label }: { content: string; label: string }) {
  return (
    <Dica conteudo={<div dangerouslySetInnerHTML={{ __html: content }} />}>
      <button
        type="button"
        aria-label={label}
        className="inline-flex opacity-[.85] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-white"
      >
        <BiInfoCircle size={15} aria-hidden="true" />
      </button>
    </Dica>
  );
}

interface PointsProps {
  label: string;
  value: number;
  help: string;
}

/** Os dois saldos em `brand-cyan-light`, como no design v2 (HOME-03), com o "i" da ajuda (AJ2-01, AJ2-02).
    D3: 10,5px não tem degrau no Tailwind. */
function Points({ label, value, help }: PointsProps) {
  const formatted = value ? FormatNumber(value) : '0';

  return (
    <div className="flex items-center gap-2.5">
      <span className="flex items-center gap-1.5 text-[10.5px] font-semibold leading-tight">
        <span>
          Pontos
          <br />
          {label}:
        </span>
        <InfoTip label={`Sobre os pontos ${label}`} content={help} />
      </span>
      <span className="font-display text-jj-headline font-bold text-brand-cyan-light">{formatted}</span>
    </div>
  );
}

interface ProfileCardProps {
  user: any;
  userInfo: any;
  profile?: PerfilNivel;
  groupInfo: any;
  seal?: Seal;
  points: { jogaJunto: number; realizacoes: number };
  onAvatarClick: () => void;
}

/** Cartão azul do topo da Home: saudação, dados funcionais, nível e pontos. */
export function ProfileCard({ user, userInfo, profile, groupInfo, seal, points, onAvatarClick }: ProfileCardProps) {
  const { showModal } = useModal();
  const headingId = useId();

  // Sem os dados do ciclo (um ciclo antigo em que o funci não participava), o nome do jogador (CAN-40).
  const firstName = (userInfo?.nome_guerra || user?.name)?.split(' ')[0];
  const isRede = !!user?.extra?.rede;
  const showGroup = !!groupInfo && !!profile && !isRede;
  const showLevels = !isRede && !!isGrupoValido(profile?.grupo ?? '');
  const sealName = seal ? SEAL_NAMES[sealKey(seal)] : undefined;

  return (
    <section
      aria-labelledby={headingId}
      // D3: 34px e 26px não têm degrau no Tailwind.
      className="flex flex-col items-center gap-6 rounded-jj-xl bg-brand-blue px-6 py-6 text-brand-white sm:flex-row sm:items-stretch sm:gap-10 sm:px-[34px] sm:pb-[26px] sm:pt-7"
    >
      {/* Coluna da foto: avatar no topo do cartão e, logo abaixo, "Editar foto" (AJH-01, PH2-02).
          D3: 148px não tem degrau no Tailwind, e max-w não tem escala de spacing no 3.3. */}
      <div className="flex w-full max-w-[148px] shrink-0 flex-col items-center gap-2 sm:w-[148px] sm:justify-start">
        <div className="flex">
          {/* Anel em degradê das cores claras da marca em volta da foto, como no design:
              menta à direita, lilás embaixo, rosa à esquerda e creme no topo.
              D3: 148px e 5px não têm degrau no Tailwind. */}
          <div className="relative h-28 w-28 rounded-full bg-[conic-gradient(from_90deg,var(--brand-green-light),var(--brand-purple-light),var(--brand-pink-light),var(--brand-yellow-light),var(--brand-green-light))] p-1 sm:h-[148px] sm:w-[148px] sm:p-[5px]">
            {/* O UserAvatar traz a foto com tabindex="-1": o clique focava a foto, e o modal de avatar
                devolvia o foco a ela ao fechar. O mousedown põe o foco no botão; o click segue igual. */}
            <button
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                e.currentTarget.focus();
              }}
              onClick={onAvatarClick}
              aria-label="Editar avatar"
              className="block h-full w-full rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue-dark"
            >
              <UserAvatar data={{ ...user, ...user?.extra, ariaHidden: false }} responsive={true} />
            </button>

            {/* z-20: o UserAvatar sem foto desenha o círculo com z-10 e cobriria o selo.
                D3: 11px e 9px não têm degrau no Tailwind. */}
            {seal && sealName && (
              <Link
                to="/selos"
                aria-label={`Ver meus selos. Selo mais recente: ${sealName}${seal.cycle ? `, ciclo ${seal.cycle}` : ''}`}
                className="absolute bottom-[11px] right-[9px] z-20 drop-shadow-jj-avatar focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-white"
              >
                <span aria-hidden="true">
                  <Selo selo={capitalize(sealKey(seal))} size={43} cycle={seal.cycle} />
                </span>
              </Link>
            )}
          </div>
        </div>

        {/* Abre a edição de avatar, como a foto; o perfil fica no "Ver perfil" (AJH-01). */}
        <button
          type="button"
          onClick={onAvatarClick}
          className="w-full rounded-jj-md py-1.5 font-display text-jj-body font-bold underline underline-offset-2 transition-colors duration-jj-fast hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-white"
        >
          Editar foto
        </button>
      </div>

      {/* Coluna da direita: saudação, dados, nível e pontos, todos na mesma margem. Sem `gap`: o espaço até os
          pontos é só a margem deles, os 30px do design (PH2-03). */}
      <div className="flex w-full min-w-0 flex-1 flex-col">
        <div>
          <Humanograma data={{ ...userInfo, _id: userInfo?.chave }}>
            <h1
              id={headingId}
              className="font-display text-jj-display-sm font-extrabold leading-none text-brand-yellow sm:text-jj-display"
            >
              Olá, {firstName}
            </h1>
          </Humanograma>
          <p className="mb-3.5 mt-1 font-display text-jj-h2 font-bold leading-tight sm:text-jj-headline">
            Juntos fazemos muito mais!
          </p>

          {/* Numa linha no desktop; em tela estreita quebra entre os dados, sem rolagem lateral (WCAG 1.4.10). */}
          <p className="mb-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className={DADO_DO_FUNCI}>
              Matrícula: <b className={VALOR_DO_FUNCI}>{user?._id}</b>
            </span>
            <span aria-hidden="true" className="opacity-50">
              /
            </span>
            <span className={DADO_DO_FUNCI}>
              Função atual: <b className={VALOR_DO_FUNCI}>{userInfo?.funcao}</b>
            </span>
            <span aria-hidden="true" className="opacity-50">
              /
            </span>
            <span className={DADO_DO_FUNCI}>
              Tipo de Acordo de Trabalho Conexão: <b className={VALOR_DO_FUNCI}>{userInfo?.tipo_de_acordo}</b>
            </span>
          </p>

          {showGroup && (
            <div className="mb-3.5 flex flex-wrap items-center gap-3">
              {/* SPEC_DEVIATION: o design usa véu branco de 15% sobre o azul (3,78:1 com texto branco de
                  11px). Reason: WCAG 1.4.3; fundo brand-blue-dark, como a pílula do prefixo no Feed.
                  D3: 13px não tem degrau no Tailwind. */}
              {/* Pílula e "(N funcis)" abrem o mesmo detalhe do grupo: um botão só, uma parada de Tab (AJH-04).
                  O nome acessível começa pelo texto visível (WCAG 2.5.3) e termina dizendo a ação. */}
              <button
                type="button"
                onClick={() => showModal('group-detail-extrato')}
                className="flex flex-wrap items-center gap-3 rounded-jj-pill text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-white"
              >
                <span className="rounded-jj-pill bg-brand-blue-dark px-[13px] py-1.5 text-jj-label font-bold uppercase">
                  Grupo: {profile?.grupo}
                </span>
                {profile?.grupoTotal != null && (
                  <span className="text-xs underline underline-offset-2">
                    {' '}
                    ({profile.grupoTotal} <b>funcis</b>)
                  </span>
                )}
                <span className="sr-only">. Ver detalhes do grupo</span>
              </button>
              <InfoTip
                label="Sobre o grupo"
                content="Essa informação poderá ser alterada até a divulgação do resultado do ciclo."
              />
            </div>
          )}

          {showLevels && (
            <>
              <p className="mb-2.5 flex items-center gap-2 text-jj-body-sm font-bold">
                <span>
                  Nível: <span>{getLevelTitle(profile?.nivel as levelType)}</span>
                </span>
                <InfoTip
                  label="Mais informações sobre os níveis de engajamento"
                  content={getLevelDescription(profile?.nivel as levelType, profile?.grupo)}
                />
              </p>

              <ul className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
                {LEVEL_BARS.map((item, index) => {
                  const reached = isLevelReached(profile?.nivel ?? '', item.level);

                  return (
                    <li key={item.level}>
                      {/* Rótulo a 13px, o tamanho do título "Nível" (AJ2-07), com o "i" da descrição (AJ2-03). */}
                      <span className="mb-1.5 flex items-center gap-1.5 text-jj-body-sm">
                        <span>
                          {item.label}
                          <span className="screen-reader-text">{reached ? ', alcançado' : ', não alcançado'}</span>
                        </span>
                        <InfoTip
                          label={`Sobre o nível ${item.label}`}
                          content={getLevelDescription(levels[index], item.level)}
                        />
                      </span>
                      <span
                        aria-hidden="true"
                        className={`block h-2 rounded-jj-pill ${reached ? 'bg-brand-yellow' : 'bg-white/[.35]'}`}
                      />
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* D3: 30px não tem degrau no Tailwind 3.3 (AJ2-08, PH2-03). */}
        <div className="mt-[30px] flex flex-wrap items-center gap-x-12 gap-y-3">
          <Points label="Joga Junto" value={points.jogaJunto} help={AJUDA_DOS_PONTOS_JOGA_JUNTO} />

          {userInfo?.tipo_de_acordo === 'HIBRIDO' && (
            <Points label="Realizações Destaque" value={points.realizacoes} help={AJUDA_DOS_PONTOS_REALIZACOES} />
          )}

          {/* Botão pequeno, como o `btnYellowSmall` do design (PH2-03). */}
          <Button variant="principal" size="sm" to="/extrato" className="ml-auto focus-visible:outline-brand-white">
            Ver perfil
          </Button>
        </div>
      </div>
    </section>
  );
}
